"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosKioskBroadcastInputSchema = exports.APosKioskPatchInputSchema = exports.APosKioskCreateInputSchema = exports.APosKioskSchema = exports.APosKioskOrientationSchema = exports.APosKioskResolutionSchema = exports.APosKioskStatusSchema = exports.KIOSK_CODE_REGEX = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../../common/timestamps.schema");
const id_schema_1 = require("../../../common/id.schema");
/**
 * Provision code format: KIOSK-XXXX-XXXX where X is the 31-char alphabet
 * with O/0/I/1/L removed (same alphabet as MD codes for advertising displays).
 *
 * Kept distinct from MD-* so a kiosk code can never be typed into the
 * advertising-app pair screen by mistake (different prefix, different audience).
 */
exports.KIOSK_CODE_REGEX = /^KIOSK-[A-HJ-KMNP-Z2-9]{4}-[A-HJ-KMNP-Z2-9]{4}$/;
exports.APosKioskStatusSchema = zod_1.z.enum([
    'pending', // provisioned but not yet activated by a device
    'active', // a device has paired and is running
    'disabled', // admin paused it — next heartbeat boots device back to pair screen
    'released', // code revoked — device can no longer activate this record
]);
exports.APosKioskResolutionSchema = zod_1.z.enum(['1080p', '4k', 'tablet']);
exports.APosKioskOrientationSchema = zod_1.z.enum(['landscape', 'portrait']);
/**
 * The Kiosk entity (one row per provisioned device).
 *
 * Generic fields (used by every kiosk type) live at the top level. Per-type
 * fields go under `config` and are validated against KIOSK_TYPES[kioskType]
 * .configSchema by the lifecycle handlers — see registry.ts.
 */
exports.APosKioskSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    kioskId: id_schema_1.EntityIdSchema,
    locationId: id_schema_1.EntityIdSchema,
    /**
     * Discriminator for the per-type config. Must match a key in KIOSK_TYPES.
     * Free string here (not a z.enum) so adding a new type later doesn't require
     * a shared-types release before backend deploy — the registry is the source
     * of truth for "what types exist."
     */
    kioskType: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1).max(100),
    description: zod_1.z.string().max(500).optional(),
    provisionCode: zod_1.z.string().regex(exports.KIOSK_CODE_REGEX),
    status: exports.APosKioskStatusSchema.default('pending'),
    // ─── Idle-mode playlist (reuses pos-advertising-service playlists) ───
    /** Playlist played when no touch for `idleAfterSeconds`. Null = fallback branded loop. */
    idlePlaylistId: id_schema_1.EntityIdSchema.nullable().default(null),
    /** Seconds of no touch before the kiosk enters idle / playlist mode. */
    idleAfterSeconds: zod_1.z.number().int().min(15).max(3600).default(60),
    /** Minutes during which a returning customer is offered "Continue your session?" */
    resumeWindowMinutes: zod_1.z.number().int().min(0).max(120).default(5),
    // ─── Device characteristics ───
    resolution: exports.APosKioskResolutionSchema.default('tablet'),
    orientation: exports.APosKioskOrientationSchema.default('portrait'),
    /**
     * Physical screen size in inches (diagonal). Optional — only the admin's
     * preference; not enforced at runtime. Used in the admin list view to make
     * "which tablet is this?" obvious at a glance.
     * Common values: 7, 8, 10.9, 11, 12.9, 13, 24, 32, 43, 55, 65.
     */
    screenSizeInches: zod_1.z.number().positive().max(120).optional(),
    // ─── Optional admin overrides ───
    /** If set, kiosk is locked to one screen (e.g. 'landing', 'mood'). */
    lockedToScreen: zod_1.z.string().optional(),
    // ─── Per-type config blob ───
    config: zod_1.z.record(zod_1.z.unknown()).default({}),
    // ─── Heartbeat / device fingerprint (set by /activate + /heartbeat) ───
    lastSeenAt: zod_1.z.number().optional(),
    ipAddress: zod_1.z.string().optional(),
    userAgent: zod_1.z.string().optional(),
    appVersion: zod_1.z.string().optional(),
    activatedAt: zod_1.z.number().optional(),
    releasedAt: zod_1.z.number().optional(),
    deregisteredAt: zod_1.z.number().optional(),
    createdBy: zod_1.z.string().min(1),
}).merge(timestamps_schema_1.TimestampsSchema);
/**
 * Create input — what super-admin supplies at provision time.
 * Server-set: tenantId, kioskId, provisionCode, status, createdBy, timestamps.
 */
exports.APosKioskCreateInputSchema = zod_1.z.object({
    locationId: id_schema_1.EntityIdSchema,
    kioskType: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1).max(100),
    description: zod_1.z.string().max(500).optional(),
    idlePlaylistId: id_schema_1.EntityIdSchema.nullable().optional(),
    idleAfterSeconds: zod_1.z.number().int().min(15).max(3600).optional(),
    resumeWindowMinutes: zod_1.z.number().int().min(0).max(120).optional(),
    resolution: exports.APosKioskResolutionSchema.optional(),
    orientation: exports.APosKioskOrientationSchema.optional(),
    screenSizeInches: zod_1.z.number().positive().max(120).optional(),
    config: zod_1.z.record(zod_1.z.unknown()).optional(),
});
/** Patch input — every field optional. */
exports.APosKioskPatchInputSchema = exports.APosKioskCreateInputSchema.partial().extend({
    lockedToScreen: zod_1.z.string().nullable().optional(),
});
/**
 * Broadcast input — bulk-assign a playlist to many kiosks.
 * Scope must specify at least one filter; empty scope is rejected.
 */
exports.APosKioskBroadcastInputSchema = zod_1.z.object({
    playlistId: id_schema_1.EntityIdSchema.nullable(),
    scope: zod_1.z.object({
        /** Apply to every kiosk in the tenant. */
        all: zod_1.z.boolean().optional(),
        locationId: id_schema_1.EntityIdSchema.optional(),
        kioskType: zod_1.z.string().optional(),
    }).refine((s) => s.all === true || !!s.locationId || !!s.kioskType, { message: 'scope must include at least one of: all, locationId, kioskType' }),
});
//# sourceMappingURL=kiosk.schema.js.map